import { Component, type ErrorInfo, type ReactNode } from 'react';
import { withTranslation, type WithTranslation } from 'react-i18next';
import { logger } from '@core/utils/logger';
import { buttonVariants } from '@shared/components';

interface Props extends WithTranslation {
  readonly children: ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundaryInner extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error('Uncaught render error', { message: error.message, componentStack: info.componentStack });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { t, children } = this.props;
    if (!this.state.hasError) return children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-8">
        <div className="max-w-md text-center">
          <h1 className="mb-2 text-2xl font-bold text-text">{t('states.appErrorTitle')}</h1>
          <p className="mb-6 text-muted">{t('states.appErrorSub')}</p>
          <button className={buttonVariants('primary', 'md')} onClick={this.handleReload}>
            {t('states.refresh')}
          </button>
        </div>
      </div>
    );
  }
}

export const ErrorBoundary = withTranslation()(ErrorBoundaryInner);
